import { useState } from "react";
import { Plus, Trash2, Leaf } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { STATO_TASK, RUOLI, formatDate } from "@/lib/constants";
import { RoleBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function TaskRow({ task, users, onChange, showPratica }) {
  const patch = async (upd) => {
    try { const { data } = await api.patch(`/tasks/${task.id}`, upd); onChange(data); } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async () => { try { await api.delete(`/tasks/${task.id}`); onChange(null); } catch (err) { toast.error(formatApiError(err)); } };
  return (
    <div className="flex flex-wrap items-center gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`task-row-${task.id}`}>
      <input type="checkbox" checked={task.stato === "completato"} onChange={(e) => patch({ stato: e.target.checked ? "completato" : "da_fare" })} className="h-4 w-4 accent-[#003087]" data-testid={`task-check-${task.id}`} />
      <div className="flex-1 min-w-[200px]">
        <div className={`text-sm ${task.stato === "completato" ? "line-through text-slate-400" : "text-slate-900"}`}>{task.titolo}</div>
        {showPratica && <div className="text-xs text-slate-500">{task.pratica_titolo}</div>}
        {task.modulo === "esg" && <a href={`/pratiche/${task.pratica_id}?tab=esg`} className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline mt-0.5" data-testid={`task-modulo-esg-${task.id}`}><Leaf size={12} /> Apri il modulo Relazione ESG</a>}
      </div>
      <RoleBadge role={task.ruolo} />
      <select value={task.stato} onChange={(e) => patch({ stato: e.target.value })} className={`h-8 rounded-full border-0 px-2.5 text-xs font-medium ${STATO_TASK[task.stato]?.cls}`} data-testid={`task-status-${task.id}`}>
        {Object.entries(STATO_TASK).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>
      <select value={task.assegnato_a || ""} onChange={(e) => patch({ assegnato_a: e.target.value || null })} className="h-8 rounded-md border border-slate-200 bg-slate-50 px-2 text-xs max-w-[160px]" data-testid={`task-assignee-${task.id}`}>
        <option value="">Non assegnato</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
      <input type="date" value={task.scadenza || ""} onChange={(e) => patch({ scadenza: e.target.value })} className="h-8 rounded-md border border-slate-200 bg-slate-50 px-2 text-xs" data-testid={`task-due-${task.id}`} title={formatDate(task.scadenza)} />
      <button onClick={remove} className="text-slate-300 hover:text-red-600 transition-colors" data-testid={`task-delete-${task.id}`}><Trash2 size={14} /></button>
    </div>
  );
}

export function TaskList({ pratica, tasks, setTasks, users }) {
  const [adding, setAdding] = useState(null);
  const [nuovo, setNuovo] = useState({ titolo: "", ruolo: "project_manager" });
  const update = (t, id) => setTasks(t ? tasks.map((x) => (x.id === t.id ? t : x)) : tasks.filter((x) => x.id !== id));
  const add = async (fase) => {
    if (!nuovo.titolo.trim()) return;
    try {
      const { data } = await api.post(`/pratiche/${pratica.id}/tasks`, { ...nuovo, fase });
      setTasks([...tasks, data]); setNuovo({ titolo: "", ruolo: "project_manager" }); setAdding(null);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-6" data-testid="task-list">
      {pratica.fasi.map((f) => {
        const list = tasks.filter((t) => t.fase === f.key);
        return (
          <div key={f.key} className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-medium text-slate-900">{f.nome}</h3>
              <span className="text-xs text-slate-500">{list.filter((t) => t.stato === "completato").length}/{list.length} completati</span>
            </div>
            {list.map((t) => <TaskRow key={t.id} task={t} users={users} onChange={(nt) => update(nt, t.id)} />)}
            {adding === f.key ? (
              <div className="flex flex-wrap gap-2 mt-3">
                <Input autoFocus placeholder="Titolo task" value={nuovo.titolo} onChange={(e) => setNuovo({ ...nuovo, titolo: e.target.value })} className="flex-1 min-w-[200px] bg-slate-50" data-testid="new-task-title-input" />
                <select value={nuovo.ruolo} onChange={(e) => setNuovo({ ...nuovo, ruolo: e.target.value })} className="h-10 rounded-md border border-slate-200 bg-slate-50 px-2 text-sm" data-testid="new-task-role-select">
                  {Object.entries(RUOLI).filter(([k]) => k !== "admin").map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <Button size="sm" onClick={() => add(f.key)} className="bg-[#003087]" data-testid="new-task-save-button">Aggiungi</Button>
                <Button size="sm" variant="ghost" onClick={() => setAdding(null)}>Annulla</Button>
              </div>
            ) : (
              <button onClick={() => setAdding(f.key)} className="mt-3 text-xs font-medium text-[#003087] hover:underline inline-flex items-center gap-1" data-testid={`add-task-${f.key}`}><Plus size={13} /> Aggiungi task</button>
            )}
          </div>
        );
      })}
    </div>
  );
}
